// File: frontend/src/utils/pushNotifications.ts
// Handles browser push notification permission and subscription (Ticket 2).
// Requires backend endpoints POST /api/push/subscribe and POST /api/push/unsubscribe
// (Ticket 3), and a VAPID public key configured via REACT_APP_VAPID_PUBLIC_KEY.

import { apiClient } from "../services/apiClient";

export type PushSupportStatus = "unsupported" | "denied" | "ready";

/**
 * Checks whether push notifications can be offered at all in this browser,
 * and whether permission was already denied in a previous session.
 */
export function getPushSupportStatus(): PushSupportStatus {
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    return "unsupported";
  }
  if (Notification.permission === "denied") {
    return "denied";
  }
  return "ready";
}

/**
 * Whether the current browser already has an active push subscription
 * for this site. Used to set the toggle's initial state on page load.
 */
export async function isSubscribedToPush(): Promise<boolean> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return false;
  }
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  return subscription !== null;
}

// Converts a base64url VAPID public key into the binary format the Push API expects.
// Returns a plain ArrayBuffer rather than a Uint8Array: since TypeScript 5.7 the typed
// arrays are generic over their backing buffer (Uint8Array<ArrayBufferLike>), which is
// not assignable to the BufferSource type required by applicationServerKey.
// ArrayBuffer is a valid BufferSource and is version-independent.
function urlBase64ToArrayBuffer(base64String: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return buffer;
}

/**
 * Requests notification permission, subscribes the browser to push, and
 * registers the subscription with the backend. Throws on any failure so the
 * caller (the settings toggle) can show an error and revert the switch.
 */
export async function subscribeToPushNotifications(): Promise<void> {
  const status = getPushSupportStatus();
  if (status === "unsupported") {
    throw new Error("PUSH_UNSUPPORTED");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("PUSH_PERMISSION_DENIED");
  }

  const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  if (!vapidPublicKey) {
    // Missing config is a setup problem, not something the user caused —
    // logged instead of surfaced as a permission/support error.
    console.error("REACT_APP_VAPID_PUBLIC_KEY is not set.");
    throw new Error("PUSH_NOT_CONFIGURED");
  }

  const registration = await navigator.serviceWorker.ready;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToArrayBuffer(vapidPublicKey),
    });
  }

  await apiClient.post("/api/push/subscribe", subscription.toJSON());
}

/**
 * Unsubscribes the browser from push and tells the backend to drop the
 * stored subscription. Safe to call even if there is no active subscription.
 */
export async function unsubscribeFromPushNotifications(): Promise<void> {
  if (!("serviceWorker" in navigator)) {
    return;
  }
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    return;
  }

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  await apiClient.post("/api/push/unsubscribe", { endpoint });
}